import { ButtonBase } from '@mui/material'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import { useGenerateCsvMutation } from '../../../../features/prescription'
import { useToast } from '../../../../contexts/ToastContext'
import { localStorageWrapper } from '../../../../helpers/localStorage'
import { colorTokens } from '../../../../shared/styles/theme'
import { RADIUS } from '../../../../shared/styles/radii'
import { ICON_SIZE } from '../../../../shared/styles/typography'
import { getAccent, Accent } from '../theme/brandByUser'

/** 主 CTA のピル(02-components/button.md primary・md=32px)。画面で青を使うのはここだけ
 * (userId が brandByUser に登録されていれば、そのカンパニーカラーに差し替わる)。 */
const pillSx = (accent: Accent) => ({
  height: 32,
  px: 1.75,
  gap: 0.75,
  borderRadius: `${RADIUS.full}px`,
  fontSize: 13,
  fontWeight: 500,
  flexShrink: 0,
  color: accent.primaryFg,
  background: accent.primary,
  transition: 'background 120ms',
  '&:hover': { background: accent.primaryHover },
  '&:focus-visible': { boxShadow: colorTokens.focus.ringShadow },
  '&.Mui-disabled': {
    background: colorTokens.bg.muted,
    color: colorTokens.fg.faint,
  },
})

interface Props {
  prescriptionId: string
}

/**
 * レセコンに送信(ADR-0033)。**出すかどうかは呼び出し側(`DetailFooter`)が決める** —
 * 店舗設定 `show_save_csv_button` と、遠隔入力に乗った紙の例外を 1 か所で読むため。
 * ここは押したら送るだけ。
 *
 * **押したら送る**(確認は挟まない)。bit1 の店舗ではこのボタンが全処方箋の唯一の経路で、
 * 1 日 130〜155 件を 1 件ずつ送るため、主動線に毎回の関門を置かない。
 *
 * ただし**送信は取り消せない** — 1 回押すごとに `SentFile` が 1 行増え、店舗の quick-sync-client が
 * 拾ってレセコンに同じ処方箋がもう 1 通届く。既に送ったかどうかは API に無く画面から読めないので、
 * 二重送信の手当ては backend 側(`SentFile` の露出)に置く。
 */
export const SendToReceiptComputer = ({ prescriptionId }: Props) => {
  const { showToast } = useToast()
  const generateCsv = useGenerateCsvMutation()
  const accent = getAccent(localStorageWrapper.getItem('userId'))

  const send = () =>
    generateCsv.mutate(prescriptionId, {
      onSuccess: () => showToast('送信しました。', 'success'),
      onError: () => showToast('送信できませんでした。', 'error'),
    })

  return (
    <ButtonBase
      onClick={send}
      disabled={generateCsv.isPending}
      sx={pillSx(accent)}
    >
      {/* 外へ渡す操作なので送信の紙飛行機。Outlined で線の細さを揃える(01-foundations/icons.md)。 */}
      <SendOutlinedIcon sx={{ fontSize: ICON_SIZE.md }} />
      レセコンに送信
    </ButtonBase>
  )
}
